import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAmCharts } from '#app/hooks/useAmCharts';
import { createChart, disposeChart, updateChart } from '../utils/createChart';

export const useChart = (chartId, client, props) => {
  const chartRef = useRef(null);
  const isInitializedRef = useRef(false);
  const { am4core, am4charts, am4lang_ru_RU, isReady } = useAmCharts();

  // Memoize chartProps to avoid unnecessary reference changes
  const chartProps = useMemo(
    () => ({
      items: props.items,
      allowEdit: props.allowEdit,
      type: props.type,
      incomeField: props.incomeField,
      retiredAge: props.retiredAge,
      deadAge: props.deadAge,
      tutorial: props.tutorial,
      incomeUpdateValue: props.incomeUpdateValue,
      outcomeUpdateValue: props.outcomeUpdateValue,
    }),
    [
      props.items,
      props.allowEdit,
      props.type,
      props.incomeField,
      props.retiredAge,
      props.deadAge,
      props.tutorial,
      props.incomeUpdateValue,
      props.outcomeUpdateValue,
    ]
  );

  const initializeChart = useCallback(
    (forceRecreate = false) => {
      // Создаем график только если библиотеки загружены
      if (!isReady || !am4core || !am4charts || !am4lang_ru_RU) {
        return;
      }

      // Создаем график если он не инициализирован, есть данные, или принудительно пересоздаем
      if ((!isInitializedRef.current || forceRecreate) && client && chartProps.items?.length > 0) {
        // Если пересоздаем, сначала удаляем старый график
        if (forceRecreate && isInitializedRef.current && chartRef.current) {
          disposeChart(chartRef.current);
          chartRef.current = null;
          isInitializedRef.current = false;
        }

        chartRef.current = createChart(
          am4core,
          am4charts,
          am4lang_ru_RU,
          chartId,
          client,
          chartProps
        );
        isInitializedRef.current = !!chartRef.current;
      }
    },
    [chartId, client, chartProps, isReady, am4core, am4charts, am4lang_ru_RU]
  );

  const updateChartData = useCallback(() => {
    // Если библиотеки не загружены, ничего не делаем
    if (!isReady || !am4core) {
      return;
    }

    // Если график не создан или нет данных, инициализируем его
    if (!isInitializedRef.current && client && chartProps.items?.length > 0) {
      initializeChart();
      return;
    }

    // Если график создан, обновляем данные
    if (isInitializedRef.current && chartRef.current && client && chartProps.items?.length > 0) {
      updateChart(am4core, chartRef.current, client, chartProps);
    }
  }, [client, chartProps, initializeChart, isReady, am4core]);

  const disposeChartInstance = useCallback(() => {
    if (chartRef.current) {
      disposeChart(chartRef.current);
      chartRef.current = null;
      isInitializedRef.current = false;
    }
  }, []);

  // Отслеживаем изменение allowEdit для пересоздания графика
  const prevAllowEditRef = useRef(chartProps.allowEdit);
  useEffect(() => {
    if (prevAllowEditRef.current !== chartProps.allowEdit && isInitializedRef.current && isReady) {
      // Пересоздаем график при изменении allowEdit
      initializeChart(true);
    }
    prevAllowEditRef.current = chartProps.allowEdit;
  }, [chartProps.allowEdit, initializeChart, isReady]);

  // Инициализация графика только при первой загрузке или смене chartId
  useEffect(() => {
    if (isReady) {
      initializeChart();
    }

    return () => {
      disposeChartInstance();
    };
  }, [chartId, isReady]);

  // Обновление графика при изменении данных (исключаем allowEdit, так как он обрабатывается отдельно)
  useEffect(() => {
    if (isReady) {
      updateChartData();
    }
  }, [
    client?.birth_at,
    client?.birth_at,
    chartProps.items,
    chartProps.type,
    chartProps.incomeField,
    chartProps.retiredAge,
    chartProps.deadAge,
    chartProps.tutorial,
    isReady,
    updateChartData,
  ]);

  return {
    initializeChart,
    updateChart: updateChartData,
    disposeChart: disposeChartInstance,
  };
};
